import React, { useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { Box, Grid, Dialog, DialogContent, Button, Typography, CircularProgress, Link } from '@mui/material';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { useTranslation } from 'react-i18next';
import { setPrice, setStartDate, setEndDate } from '../rcSlice';
import { priceRcApi } from '@/api/rcApi';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import RcPaymentForm from '../components/RcPaymentForm';
import RcWarrantyAccordion from '../components/RcWarrantyAccordion';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { fr } from 'date-fns/locale';
import { formatDateOnly } from '@/utils/date';

const startOfToday = () => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
};

const isDateBeforeToday = (isoStr: string | null) => {
  if (!isoStr) return false;
  const d = new Date(isoStr);
  if (isNaN(d.getTime())) return true;
  return d < startOfToday();
};

const Spacing = styled.div`
  height: 50px;
  @media (max-width: 600px) {
    height: 10px;
  }
`;

const ButtonStyled = styled(Button)`
  background-color: #2f6975;
  color: white;
  border-radius: 30px;
  width: 150px;
  height: 35px;
  font-weight: 400;
  font-size: 15px;
  &:hover {
    background-color: #26555f;
  }
  @media (max-width: 600px) {
    width: 100%;
    font-size: 13px;
    height: 30px;
  }
`;

const RecapAndPaymentStep = () => {
  const { t } = useTranslation('rc');
  const dispatch = useAppDispatch();
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [datePickerOpen, setDatePickerOpen] = useState(false);

  const { startDate, endDate, price } = useAppSelector(state => state.rc);
  const [priceLoading, setPriceLoading] = useState(true);

  const isStartDateOutdated = isDateBeforeToday(startDate);

  useEffect(() => {
    const fetchPrice = async () => {
      setPriceLoading(true);
      try {
        const response = await priceRcApi();
        dispatch(setPrice(response.data.priceAmount / 100));
      } catch (err) {
        console.error('Error fetching RC price:', err);
      } finally {
        setPriceLoading(false);
      }
    };
    if (price === null) {
      fetchPrice();
    } else {
      setPriceLoading(false);
    }
  }, [dispatch, price]);

  const formatDate = (dateStr: string | null) => formatDateOnly(dateStr) || 'XX/XX/XXXX';

  const handleDateChange = (newDate: Date | null) => {
    if (!newDate) return;
    const now = new Date();
    const selected = new Date(newDate);
    const isToday =
      selected.getFullYear() === now.getFullYear() &&
      selected.getMonth() === now.getMonth() &&
      selected.getDate() === now.getDate();

    let finalISOString: string;
    if (isToday) {
      finalISOString = now.toISOString();
    } else {
      const utcDate = new Date(Date.UTC(
        selected.getFullYear(),
        selected.getMonth(),
        selected.getDate(),
        0, 0, 0, 0
      ));
      finalISOString = utcDate.toISOString();
    }
    dispatch(setStartDate(finalISOString));

    // Recalculate end date (1 year later)
    const start = new Date(finalISOString);
    const end = new Date(Date.UTC(
      start.getUTCFullYear() + 1,
      start.getUTCMonth(),
      start.getUTCDate(),
      0, 0, 0, 0
    ));
    dispatch(setEndDate(end.toISOString()));
    setDatePickerOpen(false);
  };

  const handleSubmitContract = () => {
    // Filet de sécurité : le bouton est déjà désactivé si la date est périmée,
    // mais on revérifie au cas où l'utilisateur traîne pile à la bascule de minuit.
    if (isDateBeforeToday(startDate)) {
      setDatePickerOpen(true);
      return;
    }
    setPaymentModalOpen(true);
  };

  const parsedStartDate = startDate ? new Date(startDate) : null;

  return (
    <Box sx={{ maxWidth: 1000, mx: 'auto', p: 2 }}>
      {/* Start date selector - like habitation */}
      <Box display="flex" flexDirection="column" textAlign="center" alignItems="center" gap={1}>
        <Grid container justifyContent="center" alignItems="center" mb={isStartDateOutdated ? 0 : 2}>
          <Grid item xs={12} display="contents">
            <Typography variant="h3" fontWeight={500}>
              {t('step3.start_date_label')}
            </Typography>
            <Typography
              variant="h3"
              sx={{
                textDecoration: 'underline',
                cursor: 'pointer',
                color: isStartDateOutdated ? '#d32f2f' : 'blue',
                marginLeft: '0.5em',
              }}
              onClick={() => setDatePickerOpen(true)}
            >
              {formatDate(startDate)}
            </Typography>
            <Dialog open={datePickerOpen} onClose={() => setDatePickerOpen(false)}>
              <DialogContent>
                <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={fr}>
                  <DatePicker
                    label={t('step3.start_date')}
                    value={isStartDateOutdated ? null : parsedStartDate}
                    onChange={handleDateChange}
                    disablePast
                    slotProps={{ textField: { fullWidth: true } }}
                  />
                </LocalizationProvider>
              </DialogContent>
            </Dialog>
          </Grid>
        </Grid>
        {isStartDateOutdated && (
          <Typography
            variant="body2"
            sx={{ color: '#d32f2f', mb: 2, fontWeight: 500 }}
          >
            {t('common:errors.start_date_outdated')}
          </Typography>
        )}
      </Box>

      <Grid container spacing={2} mt={1}>
        {/* Left: Recap card */}
        <Grid item xs={12} sm={7}>
          <Box
            sx={{
              p: 3,
              borderRadius: 3,
              backgroundColor: 'rgba(47, 105, 117, 0.15)',
              color: '#2f3e46',
            }}
          >
            <Typography variant="h6" fontWeight={600} sx={{ textTransform: 'uppercase', mb: 2 }}>
              {t('step3.recap_title')}
            </Typography>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
              <CalendarTodayIcon sx={{ color: '#2f6975', fontSize: 18 }} />
              <Typography variant="body2" sx={{ textTransform: 'uppercase' }}>
                <strong>{t('step3.start_date')} :</strong> {formatDate(startDate)}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
              <CalendarTodayIcon sx={{ color: '#2f6975', fontSize: 18 }} />
              <Typography variant="body2" sx={{ textTransform: 'uppercase' }}>
                <strong>{t('step3.end_date')} :</strong> {formatDate(endDate)}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <AutorenewIcon sx={{ color: '#2f6975', fontSize: 18 }} />
              <Typography variant="body2" sx={{ textTransform: 'uppercase' }}>
                {t('step3.renewal')}
              </Typography>
            </Box>
          </Box>
          {/* PDF documents */}
          <Grid container mt={0.5}>
            <Grid item xs={5} sm={3} sx={{ textAlign: 'left', sm: { marginRight: '4px' } }}>
              <Typography sx={{ fontSize: { sm: '10px', xs: '8px' } }}>
                <Link target="_blank" href="/document/tableau_garanties_rc.pdf" download="tableau_garanties_rc.pdf">
                  Tableau des garanties.pdf
                </Link>
              </Typography>
            </Grid>
            <Grid item xs={3} sm={2} sx={{ textAlign: 'left' }}>
              <Typography sx={{ fontSize: { sm: '10px', xs: '8px' } }}>
                <Link target="_blank" href="/document/DIPa_rc.pdf" download="DIPa_rc.pdf">
                  Fiche IPID.pdf
                </Link>
              </Typography>
            </Grid>
            <Grid item xs={4} sm={3} sx={{ textAlign: 'left' }}>
              <Typography sx={{ fontSize: { sm: '10px', xs: '8px' } }}>
                <Link target="_blank" href="/document/conditions_generales_rc.pdf" download="conditions_generales_rc.pdf">
                  {'Conditions générales.pdf'}
                </Link>
              </Typography>
            </Grid>
          </Grid>
        </Grid>

        <Grid item xs={0} sm={1} />

        {/* Right: Price + subscribe button */}
        <Grid item xs={12} sm={4}>
          <Box sx={{ textAlign: 'center' }}>
            {priceLoading ? (
              <CircularProgress size={24} />
            ) : price !== null ? (
              <Typography fontSize={{ xs: '28px', sm: '36px' }} fontWeight={400}>
                {price.toFixed(2).replace('.', ',')}€
              </Typography>
            ) : null}
            <ButtonStyled
              disabled={!startDate || priceLoading || isStartDateOutdated}
              onClick={handleSubmitContract}
              variant="contained"
              sx={{ mt: 2 }}
            >
              {t('step3.subscribe')}
            </ButtonStyled>
          </Box>
        </Grid>
      </Grid>

      {/* Payment dialog */}
      <Dialog open={paymentModalOpen} onClose={() => setPaymentModalOpen(false)} maxWidth="sm" fullWidth>
        <DialogContent>
          {startDate && endDate && (
            <RcPaymentForm startDate={startDate} endDate={endDate} />
          )}
        </DialogContent>
      </Dialog>

      <Spacing />

      {/* Warranties section */}
      <Grid container spacing={2} mt={1}>
        <Grid item xs={12}>
          <RcWarrantyAccordion />
          <Grid container mt={0.5}>
            <Grid item xs={5} sm={3} sx={{ textAlign: 'left' }}>
              <Typography sx={{ fontSize: { sm: '10px', xs: '8px' } }}>
                <Link target="_blank" href="/document/tableau_garanties_rc.pdf" download="tableau_garanties_rc.pdf">
                  Tableau des garanties.pdf
                </Link>
              </Typography>
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={0} sm={9} />
        <Grid item xs={12} sm={3} mt={0}>
          <Grid container direction="column" alignItems="right" spacing={2}>
            <Grid item xs={12}>
              {price !== null && (
                <Typography fontSize={{ xs: '18px', sm: '28px' }} fontWeight={400}>
                  {price.toFixed(2).replace('.', ',')}€
                </Typography>
              )}
            </Grid>
            <Grid item xs={12}>
              <ButtonStyled
                disabled={!startDate || priceLoading || isStartDateOutdated}
                onClick={handleSubmitContract}
                variant="contained"
              >
                {t('step3.subscribe')}
              </ButtonStyled>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Box>
  );
};

export default RecapAndPaymentStep;
